import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { reauthenticate } from '../api/endpoints';
import { errorMessageRu } from '../api/errors';
import { Banner } from '../components/Banner';
import { useSession } from './session-context';

/**
 * Re-authentication modal.
 *
 * Admin actions, anything touching live, and every destructive command require
 * a *recent* authentication (see `REAUTH_MAX_AGE_MS` on the server). When the
 * API answers `REAUTH_REQUIRED` or `TOTP_REQUIRED`, the caller awaits
 * `promptReauth()`; this modal collects the password (and TOTP), refreshes the
 * session's reauth timestamp, and the caller retries the original command.
 */
export function ReauthModal(): ReactElement | null {
  const { reauthRequest, resolveReauth } = useSession();
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorRu, setErrorRu] = useState<string | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);

  const open = reauthRequest !== null;

  useEffect(() => {
    if (open) {
      setPassword('');
      setTotpCode('');
      setErrorRu(null);
      passwordRef.current?.focus();
    }
  }, [open]);

  if (reauthRequest === null) return null;

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setBusy(true);
    setErrorRu(null);
    const trimmed = totpCode.trim();
    reauthenticate({ password, ...(trimmed === '' ? {} : { totpCode: trimmed }) })
      .then(() => {
        setBusy(false);
        resolveReauth(true);
      })
      .catch((error: unknown) => {
        setBusy(false);
        setErrorRu(errorMessageRu(error));
      });
  };

  return (
    <div className="dialog-backdrop">
      <form
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reauth-title"
        onSubmit={onSubmit}
      >
        <h2 className="dialog__title" id="reauth-title">
          Повторная аутентификация
        </h2>
        <div className="dialog__body">
          <p>{reauthRequest.reasonRu}</p>
          <Banner tone="info">
            Подтвердите, что за панелью по-прежнему вы. Сессия не будет завершена.
          </Banner>

          {errorRu !== null && (
            <Banner tone="danger" title="Подтверждение не принято.">
              {errorRu}
            </Banner>
          )}

          <div className="field">
            <label className="field__label" htmlFor="reauth-password">
              Пароль
            </label>
            <input
              id="reauth-password"
              type="password"
              autoComplete="current-password"
              ref={passwordRef}
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="reauth-totp">
              Код двухфакторной аутентификации
            </label>
            <input
              id="reauth-totp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              value={totpCode}
              onChange={(event) => {
                setTotpCode(event.target.value);
              }}
            />
            <span className="field__hint">
              Оставьте пустым, если двухфакторная аутентификация не включена.
            </span>
          </div>
        </div>

        <div className="dialog__actions">
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() => {
              resolveReauth(false);
            }}
          >
            Отмена
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy || password === ''}>
            {busy ? 'Проверка…' : 'Подтвердить'}
          </button>
        </div>
      </form>
    </div>
  );
}
